import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import {
  LayoutDashboard, Wallet, Send, ArrowDownToLine, Users, Receipt,
  ShieldCheck, Bell, Settings as SettingsIcon, LogOut, Menu, X,
  Search, Shield, ChevronDown, ArrowUpRight,
  Building2, FileText, BarChart2, UserCheck, Activity,
} from "lucide-react";
import Logo from "@/components/Logo";
import IdleLogoutDialog from "@/components/IdleLogoutDialog";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { api } from "@/lib/api";

const ez = mmEase.expo;

const NAV_MAIN = [
  { to: "/app/dashboard",     label: "Dashboard",    icon: LayoutDashboard,  testid: "nav-dashboard",     color: "var(--color-success)" },
  { to: "/app/wallet",        label: "Wallet",       icon: Wallet,           testid: "nav-wallet",        color: "#06B6D4" },
  { to: "/app/send",          label: "Send Money",   icon: Send,             testid: "nav-send",          color: "var(--color-success)" },
  { to: "/app/receive",       label: "Receive",      icon: ArrowDownToLine,  testid: "nav-receive",       color: "#7C3AED" },
  { to: "/app/beneficiaries", label: "Recipients",   icon: Users,            testid: "nav-beneficiaries", color: "#06B6D4" },
  { to: "/app/transactions",  label: "Transactions", icon: Receipt,          testid: "nav-transactions",  color: "var(--color-warning)" },
  { to: "/app/kyc",           label: "Identity",     icon: ShieldCheck,      testid: "nav-kyc",           color: "var(--color-success)" },
];

const NAV_FOOT = [
  { to: "/app/notifications", label: "Notifications", icon: Bell,         testid: "nav-notifications" },
  { to: "/app/settings",      label: "Settings",      icon: SettingsIcon, testid: "nav-settings" },
];

const NAV_BUSINESS = [
  { to: "/app/business/dashboard",    label: "Biz Dashboard",   icon: Building2,    testid: "nav-biz-dashboard",    color: "#10B981" },
  { to: "/app/business/profile",      label: "Business Profile", icon: UserCheck,    testid: "nav-biz-profile",      color: "#7C3AED" },
  { to: "/app/business/compliance",   label: "Compliance",       icon: ShieldCheck,  testid: "nav-biz-compliance",   color: "#06B6D4" },
  { to: "/app/business/documents",    label: "Documents",        icon: FileText,     testid: "nav-biz-documents",    color: "#F59E0B" },
  { to: "/app/business/team",         label: "Team",             icon: Users,        testid: "nav-biz-team",         color: "#7C3AED" },
  { to: "/app/business/statements",   label: "Statements",       icon: BarChart2,    testid: "nav-biz-statements",   color: "#06B6D4" },
  { to: "/app/business/activity",     label: "Activity",         icon: Activity,     testid: "nav-biz-activity",     color: "#10B981" },
];

const itemV = {
  hidden: { opacity: 0, x: -14 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.32, ease: ez } },
};

function NavItem({ item, active, onClick }) {
  return (
    <motion.div variants={itemV}>
      <NavLink
        to={item.to}
        data-testid={item.testid}
        className="nav-link"
        data-active={active ? "true" : "false"}
        onClick={onClick}
      >
        <span
          className="w-[26px] h-[26px] rounded-lg grid place-items-center shrink-0 transition-all"
          style={active ? { background: `${item.color}1A`, color: item.color } : {}}
        >
          <item.icon size={14} strokeWidth={active ? 2.2 : 1.8} />
        </span>
        <span>{item.label}</span>
        {active && (
          <motion.span
            layoutId="nav-dot"
            className="ml-auto w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: item.color || "var(--color-success)" }}
            transition={{ type: "spring", stiffness: 400, damping: 32 }}
          />
        )}
      </NavLink>
    </motion.div>
  );
}

function FxBar() {
  const [rates, setRates] = useState([]);
  useEffect(() => {
    api.get("/fx/rates").then((r) => {
      if (Array.isArray(r.data)) setRates(r.data.slice(0, 8));
    }).catch(() => {});
  }, []);
  if (!rates.length) return null;
  return (
    <div className="border-t border-border/50 overflow-x-auto scrollbar-none h-[26px] flex items-center bg-secondary/50">
      <div className="flex whitespace-nowrap">
        {rates.map((r, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-5 text-[10.5px] font-mono text-muted-foreground">
            <span className="font-semibold text-navy/75">
              1 {r.base} = {Number(r.rate).toFixed(4)} {r.target}
            </span>
            <span className="w-px h-3 bg-border/60" />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/notifications").then(({ data }) => {
      const count = Array.isArray(data)
        ? data.filter((n) => !n.read).length
        : (data.unread_count ?? 0);
      setUnreadCount(count);
    }).catch(() => {});
  }, []);

  const close = () => setOpen(false);
  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex bg-background">
      <IdleLogoutDialog />

      {/* ── Sidebar ──────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
          />
        )}
      </AnimatePresence>

      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 z-40 h-screen w-[256px] flex flex-col transition-transform duration-300 glass-nav ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald/50 to-transparent" />

        {/* Logo row */}
        <motion.div
          className="px-5 py-5 flex items-center justify-between"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, ease: ez }}
        >
          <Logo variant="dark" size="md" />
          <button
            data-testid="sidebar-close"
            className="lg:hidden w-7 h-7 rounded-lg grid place-items-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
            onClick={close}
          >
            <X size={16} />
          </button>
        </motion.div>

        {/* Send CTA */}
        <div className="px-4 pb-4">
          <Link
            to="/app/send"
            onClick={close}
            className="btn-primary w-full text-[13px] py-2.5 rounded-xl flex items-center gap-2"
          >
            <Send size={13} strokeWidth={2.2} />
            Send money
            <ArrowUpRight size={12} className="ml-auto opacity-60" />
          </Link>
        </div>

        <div className="mx-4 mb-3 h-px bg-white/[0.06]" />

        {/* Main nav */}
        <motion.nav
          className="px-3 flex flex-col gap-0.5 flex-1 overflow-y-auto scrollbar-none pb-2"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.048, delayChildren: 0.1 } } }}
        >
          <p className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-white/28 font-semibold select-none">Menu</p>

          {NAV_MAIN.map((n) => (
            <NavItem
              key={n.to}
              item={n}
              active={location.pathname.startsWith(n.to)}
              onClick={close}
            />
          ))}

          {user?.account_type === "business" && (
            <>
              <p className="px-3 mt-4 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-white/28 font-semibold select-none">Business</p>
              {NAV_BUSINESS.map((n) => (
                <NavItem
                  key={n.to}
                  item={n}
                  active={location.pathname.startsWith(n.to)}
                  onClick={close}
                />
              ))}
            </>
          )}

          {isAdmin && (
            <>
              <p className="px-3 mt-4 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-white/28 font-semibold select-none">Admin</p>
              <motion.div variants={itemV}>
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className="nav-link"
                  data-active={location.pathname.startsWith("/admin") ? "true" : "false"}
                  onClick={close}
                >
                  <span className="w-[26px] h-[26px] rounded-lg grid place-items-center">
                    <Shield size={14} strokeWidth={1.8} />
                  </span>
                  Admin Console
                </NavLink>
              </motion.div>
            </>
          )}
        </motion.nav>

        {/* Bottom links */}
        <div className="px-3 pt-3 border-t border-white/[0.06] space-y-0.5">
          {NAV_FOOT.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className="nav-link"
              data-active={location.pathname === n.to ? "true" : "false"}
              onClick={close}
            >
              <span className="w-[26px] h-[26px] rounded-lg grid place-items-center relative">
                <n.icon size={14} strokeWidth={1.8} />
                {n.to === "/app/notifications" && unreadCount > 0 && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-emerald rounded-full" />}
              </span>
              {n.label}
            </NavLink>
          ))}
          <button
            data-testid="nav-logout"
            onClick={handleLogout}
            className="nav-link w-full text-left"
          >
            <span className="w-[26px] h-[26px] rounded-lg grid place-items-center">
              <LogOut size={14} strokeWidth={1.8} />
            </span>
            Sign out
          </button>
        </div>

        {/* Profile mini card */}
        <Link
          to="/app/profile"
          className="mx-3 my-3 p-3 rounded-xl flex items-center gap-2.5 bg-white/[0.05] border border-white/[0.07] hover:bg-white/[0.09] transition-all group"
        >
          <div
            className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0"
            style={{ background: user?.avatar_color || "var(--color-success)" }}
          >
            {initials(user?.full_name || "ME")}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-white/88 truncate">{user?.full_name || "Account"}</p>
            <p className="text-[10px] text-white/38 truncate">{user?.email}</p>
          </div>
          <ChevronDown size={12} className="text-white/28 group-hover:text-white/55 shrink-0 transition-colors" />
        </Link>
      </aside>

      {/* ── Main content ──────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">

        {/* Topbar */}
        <motion.header
          className="sticky top-0 z-20 glass border-b border-white/40"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: ez }}
        >
          <div className="px-4 sm:px-6 lg:px-8 h-[58px] flex items-center gap-3">
            <button
              data-testid="sidebar-open"
              className="lg:hidden w-8 h-8 rounded-xl grid place-items-center text-navy/70 hover:text-navy hover:bg-secondary transition-all"
              onClick={() => setOpen(true)}
            >
              <Menu size={19} />
            </button>

            <label
              className="hidden md:flex items-center gap-2 px-3 py-2 bg-secondary/70 border border-border/60 rounded-xl w-64 cursor-text transition-all hover:border-border focus-within:border-emerald focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(16,185,129,0.12)]"
              htmlFor="topbar-search"
            >
              <Search size={13} className="text-muted-foreground shrink-0" />
              <input
                id="topbar-search"
                data-testid="topbar-search"
                placeholder="Search…"
                className="bg-transparent outline-none text-sm flex-1 text-navy placeholder:text-muted-foreground"
              />
              <kbd className="hidden lg:inline text-[10px] text-muted-foreground/70 border border-border/80 rounded px-1 py-0.5 font-mono">⌘K</kbd>
            </label>

            <div className="flex-1" />

            <Link
              to="/app/send"
              data-testid="topbar-send-cta"
              className="hidden sm:inline-flex items-center gap-1.5 bg-navy text-white text-[12px] font-semibold px-3.5 py-2 rounded-full hover:bg-navy-light transition-all hover:-translate-y-px"
            >
              <Send size={12} strokeWidth={2.2} />
              Send
            </Link>

            <Link
              to="/app/notifications"
              className="relative w-8 h-8 grid place-items-center text-navy/65 hover:text-navy hover:bg-secondary rounded-xl transition-all"
              data-testid="topbar-bell"
            >
              <Bell size={16} strokeWidth={1.8} />
              {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-emerald rounded-full" />}
            </Link>

            <Link
              to="/app/profile"
              className="flex items-center gap-2 group"
              data-testid="topbar-profile"
            >
              <div
                className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-bold ring-2 ring-transparent group-hover:ring-emerald/30 transition-all"
                style={{ background: user?.avatar_color || "var(--color-success)" }}
              >
                {initials(user?.full_name || "ME")}
              </div>
              <span className="hidden lg:block text-[13px] font-semibold text-navy truncate max-w-[110px]">
                {user?.full_name?.split(" ")[0]}
              </span>
            </Link>
          </div>
          <FxBar />
        </motion.header>

        {/* Scrollable area */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.main
            key={location.pathname}
            className="flex-1 px-4 sm:px-6 lg:px-10 py-7 max-w-[1440px] w-full mx-auto"
            style={{ minHeight: "calc(100vh - 84px)" }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.26, ease: ez }}
          >
            {children}
          </motion.main>
        </AnimatePresence>

        <footer className="px-6 lg:px-10 py-5 border-t border-border/50 text-[11px] text-muted-foreground flex flex-col sm:flex-row gap-1.5 justify-between">
          <span>© {new Date().getFullYear()} MoneyMeet Pay · Global Payments. One Wallet.</span>
          <span className="flex gap-4 items-center">
            <span>
              Identity-verified transfers
              {process.env.NODE_ENV !== "production" && (
                <span className="ml-1 opacity-60">(dev)</span>
              )}
            </span>
            <span className="text-emerald font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
              All systems operational
            </span>
          </span>
        </footer>
      </div>
    </div>
  );
}
